import { useMultipleFeatureFlags } from '@agentnet/feature-flags';
import { PageHeader } from '@fluentsms/agentnet-web-components';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { MarketplaceProduct } from 'api/admin/types';
import { KBLinks } from 'features/constants';
import { TITLE_MONITORING_PRODUCT_CODE } from 'features/help/constants';
import { useSearchParams } from 'features/help/hooks/useSearchParams';
import { useMessagingQuery } from 'features/help/queries/useMessagingQuery';
import { Message } from 'features/help/types/messagingCollection';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { FC, useEffect, useMemo, useState } from 'react';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { IconsMap } from 'ui-kit/icons/MarketplaceIcons';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { normalizeServiceName } from 'utilities/utilities';
import FeatureDetails from './FeatureDetails';
import { IntegrationCard, StatusChipConfig } from './IntegrationCard';

const getChipDetails = (service: MarketplaceProduct, cardTestId: string): StatusChipConfig[] => [
  {
    label: 'First American',
    variant: 'success',
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    testId: `${cardTestId}-first-american-chip`,
  },
  {
    label: 'Other Underwriters',
    variant: service.isEnabled ? 'success' : 'warning',
    icon: service.isEnabled ? <CheckCircleOutlineIcon fontSize="small" /> : <LockOutlinedIcon fontSize="small" />,
    testId: service.isEnabled
      ? `${cardTestId}-other-underwriters-enabled-chip`
      : `${cardTestId}-other-underwriters-disabled-chip`,
  },
];

const getStatusChips = (service: MarketplaceProduct, cardTestId: string): StatusChipConfig[] | undefined => {
  if (service?.productCode === TITLE_MONITORING_PRODUCT_CODE) {
    return undefined;
  }

  return getChipDetails(service, cardTestId);
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentWrap: {
      margin: theme.spacing(3),
    },
    verticalSpacing: {
      marginTop: theme.spacing(3),
    },
    cardStyles: {
      height: '260px',
    },
  }),
);

const IntegrationsFeatures: FC = () => {
  const classes = useStyles();
  const [selectedService, setSelectedService] = useState<MarketplaceProduct | null>(null);
  const [allDescriptions, setAllDescriptions] = useState<Record<string, Message | null>>({});
  const searchParams = useSearchParams<{ productCode?: string }>();

  const {
    isMarketPlaceEnabled,
    fetchMarketPlaceServices,
    fetchMarketPlaceServicesResults: marketPlaceServices,
    fetchMarketPlaceStatus,
    fetchTitleMonitoringConfig,
    fetchTitleMonitoringConfigResults,
  } = useMarketPlace();

  const [titleMonitoring, titleMonitoringFirmAdmin, nonFaUw] = useMultipleFeatureFlags(
    FeatureFlag.TITLE_MONITORING_ENABLED,
    FeatureFlag.TITLE_MONITORING_FIRM_ADMIN_ENABLED,
    FeatureFlag.NON_FA_UW_ANS_679_ENABLED,
  );
  const isTitleMonitoringVisible = titleMonitoring.enabled && titleMonitoringFirmAdmin.enabled;
  const isNonFaUwEnabled = !!nonFaUw.value;
  const areFeatureFlagsReady = titleMonitoring.ready && titleMonitoringFirmAdmin.ready && nonFaUw.ready;

  const fetchMarketPlaceServicesResults = useMemo(
    () =>
      isTitleMonitoringVisible || !marketPlaceServices
        ? marketPlaceServices
        : marketPlaceServices.filter((service) => service.productCode !== TITLE_MONITORING_PRODUCT_CODE),
    [isTitleMonitoringVisible, marketPlaceServices],
  );

  useEffect(() => {
    if (isMarketPlaceEnabled) fetchMarketPlaceServices();
  }, [isMarketPlaceEnabled]);

  useEffect(() => {
    let isActive = true;
    if (searchParams?.productCode && fetchMarketPlaceServicesResults) {
      const matchedService = fetchMarketPlaceServicesResults.find(
        (service: MarketplaceProduct) => service.productCode === searchParams.productCode,
      );
      if (isActive && matchedService) {
        setSelectedService(matchedService);
      }
    } else if (selectedService) {
      const updatedService = fetchMarketPlaceServicesResults?.find(
        (service: MarketplaceProduct) => service.productCode === selectedService.productCode,
      );
      updatedService && setSelectedService(updatedService);
    }
    return () => {
      isActive = false;
    };
  }, [fetchMarketPlaceServicesResults, searchParams]);

  useEffect(() => {
    if (fetchMarketPlaceServicesResults) {
      const preload = async () => {
        const slugs = fetchMarketPlaceServicesResults?.map(
          (service) => `feature-${service.productName.toLowerCase().replace(/\s+/g, '-')}`,
        );
        const entries = await Promise.all(slugs?.map((slug) => useMessagingQuery(slug)));
        const result = Object.fromEntries(
          fetchMarketPlaceServicesResults.map((service, idx) => [service.productCode, entries[idx].data]),
        );
        setAllDescriptions(result);
      };

      preload();
    }
  }, [fetchMarketPlaceServicesResults]);

  useEffect(() => {
    const hasTitleMonitoring = fetchMarketPlaceServicesResults?.some(
      (service) => service.productCode === TITLE_MONITORING_PRODUCT_CODE,
    );
    if (hasTitleMonitoring) {
      fetchTitleMonitoringConfig();
    }
  }, [fetchMarketPlaceServicesResults]);

  const ServiceIcon = ({ serviceName }: { serviceName: string }) => {
    const normalizedName = normalizeServiceName(serviceName);
    const IconComponent = IconsMap[normalizedName] || IconsMap['default'];

    return <IconComponent />;
  };

  if (!areFeatureFlagsReady) {
    return (
      <ContentContainer mTop="150px" padding="0 0 0 16px">
        <LoadingSpinner variant="circle" status="pending" />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer fullWidth>
      <LoadingSpinner status={fetchMarketPlaceStatus} variant="linear" />
      <div className={classes.contentWrap}>
        {!selectedService ? (
          <>
            <PageHeader
              title="Add-Ons"
              subtitle="Enable and manage options for additional services available within your files."
              menuItems={[
                {
                  label: 'Knowledge Base',
                  link: KBLinks.addOns,
                },
              ]}
            />
            <Grid container spacing={1} className={classes.verticalSpacing}>
              {fetchMarketPlaceServicesResults?.map((service: MarketplaceProduct) => {
                const cardTestId = `add-ons-card-${normalizeServiceName(service?.productName)}`;

                return (
                  <Grid item sm={3} key={service?.productName}>
                    <IntegrationCard
                      testId={cardTestId}
                      title={service?.productName}
                      icon={<ServiceIcon serviceName={service?.productName} />}
                      description={service?.description}
                      category={service?.category}
                      enabled={service?.isEnabled}
                      {...(isNonFaUwEnabled && { statusChips: getStatusChips(service, cardTestId) })}
                      onClick={() => setSelectedService(service)}
                      className={classes.cardStyles}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        ) : (
          <FeatureDetails
            goBackAction={() => setSelectedService(null)}
            selectedService={selectedService}
            marketplaceServices={fetchMarketPlaceServicesResults}
            refreshServiceList={fetchMarketPlaceServices}
            ServiceIcon={<ServiceIcon serviceName={selectedService?.productName} />}
            allDescriptions={allDescriptions}
            titleMonitoringConfig={fetchTitleMonitoringConfigResults}
          />
        )}
      </div>
    </ContentContainer>
  );
};

export default IntegrationsFeatures;
